// @ts-ignore
import React, { type ReactNode } from 'react';
import '../CyberModal.css';

interface CyberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed?: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  proceedLabel?: string;
  cancelLabel?: string;
  carouselImages?: string[]; // 🆕 optional carousel images
}

export const CyberModal: React.FC<CyberModalProps> = ({
                                                        isOpen,
                                                        onClose,
                                                        onProceed,
                                                        title = "Cyber Modal",
                                                        subtitle = "Welcome to the Cyberpunk World",
                                                        children,
                                                        proceedLabel = "Proceed",
                                                        cancelLabel = "Sluiten",
                                                        carouselImages = [],
                                                      }) => {
  const [glitchActive, setGlitchActive] = React.useState(false);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [fullscreenImage, setFullscreenImage] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (fullscreenImage) {
          setFullscreenImage(null);
        } else {
          onClose();
        }
      } else if (e.key === 'Enter') {
        onProceed?.();
      }
    };

    const glitchTimer = setTimeout(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 2000);
    }, 1500);

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      clearTimeout(glitchTimer);
    };
  }, [isOpen, onClose, onProceed, fullscreenImage]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) =>
        prev === 0 ? carouselImages.length - 1 : prev - 1
    );
  };

  if (!isOpen) return null;

  return (
      <>
      <div className="cyber-modal-overlay" onClick={onClose}>
        <div className="cyber-modal" onClick={(e) => e.stopPropagation()}>
          <section className="cyber-modal__body">
            <div className="body__backdrop">
              <div className="backdrop">
                <div className="version">v001.e1349837856</div>
                <div className="corner"></div>
              </div>
            </div>
            <div className="body__content">
              <h2>
                <span>{title}</span>
                <br />
                <span className="subtitle">{subtitle}</span>
              </h2>

              <div className="body__text">{children}</div>

              <div className={`modal__glitch ${glitchActive ? 'active' : ''}`}>
                <h2>
                  <span>{title}</span>
                </h2>
                <div className="body__text">{children}</div>
              </div>
              {carouselImages.length > 0 && (
                  <div className="cyber-carousel">
                    <button className="carousel-btn left" onClick={prevSlide}>
                      ‹
                    </button>
                    <div className="carousel-track">
                      {carouselImages.map((src, index) => (
                          <img
                              key={index}
                              src={src}
                              alt={`slide-${index}`}
                              className={`carousel-image ${
                                  index === currentSlide ? 'active' : ''
                              }`}
                              onClick={() => index === currentSlide && setFullscreenImage(src)}
                              style={{ cursor: index === currentSlide ? 'pointer' : 'default', pointerEvents: index === currentSlide ? 'auto' : 'none' }}
                          />
                      ))}
                    </div>
                    <button className="carousel-btn right" onClick={nextSlide}>
                      ›
                    </button>
                  </div>
              )}
            </div>
          </section>
          <div className="cyber-modal__actions">
            <button aria-label="Cancel" className="cyber-btn" onClick={onClose}>
            <span className="backdrop">
              <span className="corner"></span>
            </span>
              <kbd>esc</kbd>
              <span>{cancelLabel}</span>
            </button>
          </div>
        </div>
      </div>

      {fullscreenImage && (
        <div className="fullscreen-overlay" onClick={() => setFullscreenImage(null)}>
          <img src={fullscreenImage} alt="Fullscreen" className="fullscreen-image" />
          <button className="fullscreen-close" onClick={() => setFullscreenImage(null)}>×</button>
        </div>
      )}
      </>
  );
};

interface CyberButtonProps {
  onClick: () => void;
  label?: string;
  shortcut?: string;
}

// Trigger button component
export const CyberButton: React.FC<CyberButtonProps> = ({onClick, label = "Open", shortcut = "O",}) => {
  return (
      <button className="cyber-trigger-btn" onClick={onClick}>
      <span className="backdrop">
        <span className="corner"></span>
      </span>
        <kbd>{shortcut}</kbd>
        <span>{label}</span>
      </button>
  );
};
